import { useMemo } from "react";
import { headToHead, lastGameOf } from "@averagejoesff/shared";
import type {
  LeagueSnapshot,
  ManagerProfile,
  MatchupReport,
  PersonGame,
  ReportManager,
  SlateGame,
  StandingRow,
} from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { formatOrdinal, formatPoints, formatRecord } from "../../lib/format";
import { FactList } from "../matchups/FactList";
import { MatchupCard } from "../matchups/MatchupCard";
import { MatchupHeader } from "../matchups/MatchupHeader";
import { indexManagers } from "../matchups/managers";
import type { PeopleLookup } from "../managers/people";
import { personName, profilePath } from "../managers/people";
import { ViewerBar } from "./ViewerBar";

interface MyWeekProps {
  me: ManagerProfile;
  profiles: readonly ManagerProfile[];
  snapshot: LeagueSnapshot;
  ledger: readonly PersonGame[];
  people: PeopleLookup;
  /** This week's published Matchups report, when there is one (see `useCurrentReport`). */
  report: MatchupReport | null;
  onForget: () => void;
}

/** The visitor's own season so far, and their game this week. */
export function MyWeek({ me, profiles, snapshot, ledger, people, report, onForget }: MyWeekProps) {
  const standing = snapshot.standings.find((row) => row.personId === me.personId);
  const game = snapshot.slate.find((g) => g.personIds.includes(me.personId));
  const last = lastGameOf(ledger, me.personId);

  return (
    <>
      <ViewerBar actionLabel="Not you?" onAction={onForget}>
        Viewing as <AppLink href={profilePath(me.personId)}>{me.name}</AppLink>
      </ViewerBar>

      {standing ? (
        <dl className="payout-stats payout-stats--triple">
          <div className="payout-stats__item">
            <dt>{snapshot.season} record</dt>
            <dd>{formatRecord(standing.record.wins, standing.record.losses, standing.record.ties)}</dd>
          </div>
          <div className="payout-stats__item">
            <dt>{snapshot.isFinished ? "Finished" : "Standing"}</dt>
            <dd>{formatOrdinal(standing.rank)}</dd>
          </div>
          <div className="payout-stats__item">
            <dt>Points for</dt>
            <dd>{formatPoints(standing.pointsFor)}</dd>
          </div>
        </dl>
      ) : (
        <p className="home__line">You don't have a team in the {snapshot.season} league.</p>
      )}

      <ul className="home__lines">
        {last && (
          <li>
            Last game: {last.result === "W" ? "won" : last.result === "L" ? "lost" : "tied"}{" "}
            {formatPoints(last.points)}–{formatPoints(last.opponentPoints)} against {personName(people, last.opponentId)}{" "}
            <span className="home__when">
              ({last.season} week {last.week})
            </span>
          </li>
        )}
        {me.currentStreak && me.currentStreak.season === snapshot.season && me.currentStreak.length >= 2 && (
          <li>
            {me.currentStreak.result === "W" ? "🔥 Won" : "🧊 Lost"} {me.currentStreak.length} straight.
          </li>
        )}
        {me.titles.length > 0 && (
          <li>
            🏆 {me.titles.length === 1 ? "Champion" : `${me.titles.length}-time champion`} ({me.titles.join(", ")})
          </li>
        )}
      </ul>

      {game && standing && (
        <ThisWeek
          me={me}
          profiles={profiles}
          game={game}
          snapshot={snapshot}
          standing={standing}
          ledger={ledger}
          people={people}
          report={report}
        />
      )}
      {!game && snapshot.week !== null && !snapshot.isFinished && standing && (
        <p className="home__line">No game for you in week {snapshot.week}.</p>
      )}
    </>
  );
}

interface ThisWeekProps {
  me: ManagerProfile;
  profiles: readonly ManagerProfile[];
  game: SlateGame;
  snapshot: LeagueSnapshot;
  standing: StandingRow;
  ledger: readonly PersonGame[];
  people: PeopleLookup;
  report: MatchupReport | null;
}

function ThisWeek({ me, profiles, game, snapshot, standing, ledger, people, report }: ThisWeekProps) {
  const opponentId = game.personIds.find((id) => id !== me.personId) ?? me.personId;
  const opponentStanding = snapshot.standings.find((row) => row.personId === opponentId);
  const matchup = report?.matchups.find((m) => m.managerIds.includes(me.personId) && m.managerIds.includes(opponentId));

  return (
    <section aria-labelledby="home-this-week">
      <h3 id="home-this-week" className="feature__subhead">
        Week {game.week}
        {game.gameType === "playoffs" ? " · Playoffs" : game.gameType === "consolation" ? " · Consolation" : ""}
      </h3>
      {report && matchup ? (
        // The published Matchups report has the full story for this game.
        <MatchupCard matchup={matchup} report={report} managers={indexManagers(report)} />
      ) : (
        <article className="matchup-card">
          <MatchupHeader
            left={reportManager(standing, me)}
            right={opponentStanding && reportManager(opponentStanding, profiles.find((p) => p.personId === opponentId))}
          />
          <SeriesLine me={me.personId} opponentId={opponentId} ledger={ledger} people={people} />
        </article>
      )}
    </section>
  );
}

/** Just enough of a `ReportManager` for `MatchupHeader`. */
function reportManager(row: StandingRow, profile: ManagerProfile | undefined): ReportManager {
  return {
    id: row.personId,
    name: profile?.name ?? row.personId,
    teamName: row.teamName,
    logoUrl: row.logoUrl,
    record: row.record,
    rank: row.rank,
    pointsFor: row.pointsFor,
    championships: profile?.titles ?? [],
  };
}

function SeriesLine({ me, opponentId, ledger, people }: { me: string; opponentId: string; ledger: readonly PersonGame[]; people: PeopleLookup }) {
  const { record, gamesPlayed, lastMeeting } = useMemo(() => headToHead(ledger, me, opponentId), [ledger, me, opponentId]);
  if (gamesPlayed === 0) return <p className="matchup-card__first-meeting">First-ever meeting.</p>;
  const lead =
    record.wins === record.losses
      ? `All square at ${formatRecord(record.wins, record.losses, record.ties)}`
      : record.wins > record.losses
        ? `You lead ${formatRecord(record.wins, record.losses, record.ties)}`
        : `${personName(people, opponentId)} leads ${formatRecord(record.losses, record.wins, record.ties)}`;
  return (
    <FactList
      facts={[
        { emoji: "⚔️", text: `${lead} over ${gamesPlayed} ${gamesPlayed === 1 ? "meeting" : "meetings"}.` },
        ...(lastMeeting
          ? [
              {
                emoji: "🕑",
                text: `Last meeting: ${lastMeeting.season} week ${lastMeeting.week}, ${formatPoints(lastMeeting.points)} to ${formatPoints(lastMeeting.opponentPoints)}.`,
              },
            ]
          : []),
      ]}
    />
  );
}
